import React, { useCallback, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import Button from 'components/Button';
import Modal from 'components/Modal';
import MessageBox from 'components/MessageBox';
import AsyncSelect from 'components/AsyncSelect/AsyncSelect';
import AsyncSelectLoadMore from 'components/AsyncSelect/AsyncSelectLoadMore';
// api
import { getTreatmentProtocolsFarmPigGroups } from 'endpoints/treatmentProtocols';

const groupStatuses = ['opened', 'request_close', 'awaiting_delivery', 'scheduled_delivery'];

const CreateTreatmentProtocolModal = ({ closeModal, onContinue }) => {
  const [selectedFarm, setSelectedFarm] = useState({});
  const [selectedGroup, setSelectedGroup] = useState({});

  const hasFarm = selectedFarm && selectedFarm.value;
  const hasGroup = selectedGroup && selectedGroup.value;

  const handleFarmChange = (option) => {
    setSelectedFarm(option || {});
    setSelectedGroup({});
  };

  const handleGroupChange = (option) => {
    setSelectedGroup(option || {});
  };

  const getFarmPigGroups = useCallback((params) => {
    return getTreatmentProtocolsFarmPigGroups(selectedFarm.value, { ...params, status: groupStatuses });
  }, [selectedFarm.value]);

  const handleContinue = () => {
    if (!hasFarm) return;

    const farmId = selectedFarm.value;
    if (hasGroup) {
      const groupId = selectedGroup.value;
      onContinue(`/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols/create`);
    } else {
      onContinue(`/treatment-protocols-farms/${farmId}/treatment-protocols/create`);
    }
    closeModal();
  };

  return (
    <Modal title={<FormattedMessage id="general.createTreatmentProtocol" />}>
      <section className="modal-body">
        <MessageBox type="warning" isBorderHidden>
          <div style={{ whiteSpace: 'pre-line' }}>
            <FormattedMessage
              id="component.modal.createTreatmentProtocol.warning"
              values={{ b: (chunks) => <b>{chunks}</b> }}
            />
          </div>
        </MessageBox>

        <div className="mt-15">
          <label className="semibold mb-5">
            <FormattedMessage id="general.selectFarm" />
          </label>
          <AsyncSelect
            selected={selectedFarm}
            onChange={handleFarmChange}
            optionsPath="/farms/search"
            optionsKeys={{ value: 'id', label: 'name' }}
            requestParams={{ active: true, type: ['su', 'n', 'wf', 'f'] }}
            placeholder={<FormattedMessage id="general.selectFarm" />}
            autoLoad
          />
        </div>

        {hasFarm && (
          <div className="mt-15">
            <label className="semibold mb-5">
              <FormattedMessage id="general.selectGroup" />
              <span className="light ml-5">
                (<FormattedMessage id="general.optional" />)
              </span>
            </label>
            <AsyncSelectLoadMore
              key={selectedFarm.value}
              isClearable
              selected={selectedGroup}
              onChange={handleGroupChange}
              optionsGetter={getFarmPigGroups}
              placeholder={<FormattedMessage id="general.selectGroup" />}
              autoLoad
            />
          </div>
        )}
      </section>

      <section className="modal-footer text-right">
        <Button className="margin-right-1" onClick={closeModal}>
          <FormattedMessage id="general.button.cancel" />
        </Button>
        <Button primary disabled={!hasFarm} onClick={handleContinue}>
          <FormattedMessage id="component.modal.createTreatmentProtocol.continue" />
        </Button>
      </section>
    </Modal>
  );
};

CreateTreatmentProtocolModal.propTypes = {
  closeModal: T.func.isRequired,
  onContinue: T.func.isRequired,
};

export default connect(null, { closeModal })(CreateTreatmentProtocolModal);
